import { formatCurrency } from '~libs/dataUtils';
import { formatDateString } from '~libs/dayjs';
import { Column, Row } from '~components/table-view';

import BatchClientPaymentStatusCell from '~direct-debits/components/payment-batches/BatchClientPaymentStatusCell';
import BatchClientRetryAlertCell from '~direct-debits/components/payment-batches/BatchClientRetryAlertCell';
import BatchClientRowAction from '~direct-debits/components/payment-batches/BatchClientRowAction';
import { PaymentBatchClient } from '~direct-debits/models';

export interface BatchDetailCallbacks {
  onViewInvoices?: (clientId: number) => void;
  onRefund?: (row: PaymentBatchClient) => void;
  onReverse?: (row: PaymentBatchClient) => void;
  // Connection environment (live/sandbox) for the "Open in GoCardless" link host.
  environment?: string | null;
}

export const columnsFactory = (callbacks: BatchDetailCallbacks = {}): Column[] => [
  {
    id: 'reference',
    header: 'Item ID',
    type: 'text',
    // The payment line's `DDI-NNNNNN` reference (payment_batch_items.reference).
    deriveValue: (row) => (row.reference as string) || '—',
  },
  {
    id: 'client_name',
    header: 'Client Name',
    type: 'text',
    deriveValue: (row) => (row.client_name as string) || '—',
  },
  {
    id: 'payment_status',
    header: 'Payment Status',
    type: 'custom',
    component: BatchClientPaymentStatusCell,
    classNames: { header: 'cell-align-center', column: 'cell-align-center' },
  },
  {
    // Header-less alert column — shows an icon only for a failed-but-retrying line.
    id: 'retry_alert',
    header: '',
    type: 'custom',
    component: BatchClientRetryAlertCell,
    classNames: { header: 'cell-align-center', column: 'cell-align-center' },
  },
  {
    id: 'next_charge_date',
    header: 'Next Charge Date',
    type: 'text',
    // Next collection attempt; `-` when there is none (never failed, or terminal).
    deriveValue: (row) => (row.next_charge_date ? formatDateString(row.next_charge_date as string) : '-'),
    classNames: { header: 'cell-align-center', column: 'cell-align-center' },
  },
  {
    id: 'amount',
    header: 'Amount',
    type: 'text',
    deriveValue: (row) => formatCurrency(row.amount as string),
    classNames: { header: 'cell-align-right', column: 'cell-align-right' },
  },
  {
    id: 'invoice_count',
    header: 'Invoices',
    type: 'text',
    deriveValue: (row) => String(row.invoice_count ?? 0),
    classNames: { header: 'cell-align-center', column: 'cell-align-center' },
  },
  {
    id: 'actions',
    header: '',
    type: 'custom',
    component: BatchClientRowAction,
    componentProps: callbacks,
    classNames: { header: 'cell-align-right', column: 'cell-align-right' },
  },
];

export type BatchClientRow = Row & PaymentBatchClient;
